import {mixedSettlementLearningBlocked95} from "./mixedSettlementLearning95";
import type { DbExecutor } from "./executor";
import { generateId } from "./ids";
import { ValidationError } from "./repositories/projectRepository";
import type { ProposedCostRepository } from "./repositories/proposedCostRepository";
import type { CategoryBudgetService } from "./categoryBudgetService";
import type { ReconciliationService } from "./reconciliationService";
import {
  calibratedRate,
  compareToReference,
  deriveRates,
  projectSqftMetrics,
  referenceArithmetic,
  type CalibrationComparison,
  type CalibrationReferenceInput,
  type ProjectSqftMetrics,
  type RateDriver,
  type ReferenceArithmetic,
  type CalibratedRate,
  type ReferenceLineInput,
} from "../engine/calibration";
import { resolveCategory } from "../engine/categoryBudget";
import type { Cents } from "../domain/money";
import type { TradeId } from "../domain/trade";
import { isDimensionless, rateMatchesQuantity, rateUnitWords, surfaceOfBinding, type RateSurface, type RateUnit } from "../domain/rateUnit";
import { effectiveFeatureQuantity, geometryBindingUnit, type FeatureGeometryBinding, type QuantityUnit, type ProjectFeature } from "../domain/feature";

/**
 * ESTIMATING PROFILES (spec §77).
 *
 * The service around the pure calibration engine. What it PERSISTS is only
 * what somebody decided: profiles, calibration references (a real house's
 * real budget, line by line), the rates derived from them — each with its
 * provenance — and which profile a project estimates with. What it DERIVES
 * is everything else: arithmetic checks, comparisons, $/sq ft benchmarks.
 *
 * Two rules hold the whole feature honest:
 *
 *   THE DETAILED SCOPE STAYS THE SOURCE OF TRUTH. Applying a profile never
 *   creates scope, and a calibrated figure lands as an ORDINARY proposed
 *   line linked to an existing feature — so quotes, authorized budgets,
 *   actuals, the §75 forecast and the Capital Plan all follow through the
 *   paths that already exist. A rate is skipped, with the reason stated,
 *   wherever the detailed estimate has already priced the work.
 *
 *   NOTHING IS FORCED TO MATCH. The comparison reports differences and
 *   missing scope in both directions; there is no hidden multiplier, and
 *   recorded actuals never silently recalibrate a rate (spec §77Q).
 */

export interface EstimatingProfile {
  id: string;
  name: string;
  notes: string;
  createdAt: string;
}

export interface CalibrationReference {
  id: string;
  profileId: string;
  label: string;
  projectId: string | null;
  livingAreaSqftX100: number | null;
  purchasePriceCents: Cents | null;
  purchaseClosingCents: Cents | null;
  statedRehabCents: Cents | null;
  saleForecastCents: Cents | null;
  arvCents: Cents | null;
  sellingClosingCents: Cents | null;
  interestAllowanceCents: Cents | null;
  insuranceCents: Cents | null;
  notes: string;
  lines: ReferenceLineInput[];
}

export interface ProfileRate {
  id: string;
  profileId: string;
  categoryId: TradeId | "other";
  featureDefinitionId: string | null;
  driver: RateDriver;
  /**
   * Spec §90: what the rate is PER. Null on a pre-§90 row, which keeps the
   * old driver-only behaviour — a legacy rate is never reinterpreted.
   */
  unit: RateUnit | null;
  surface: RateSurface | null;
  rateCents: Cents;
  provenance: string;
  sourceReferenceId: string | null;
  /**
   * Spec §91-23: where the base figure came from — 'reference' (reverse-
   * engineered from a calibration budget) or 'manual' (somebody typed the
   * price they normally budget). Null on a pre-§91 row. Both feed the SAME
   * profile: there is no manual price book competing with a derived one.
   */
  origin: RateOrigin;
}

/** Spec §91-18/23: how a base rate got its number. */
export type RateOrigin = "reference" | "manual" | null;

export interface ReferenceLineDraft {
  description: string;
  amountCents: Cents;
  categoryId: TradeId | "other";
  quantityX100?: number | null;
  quantityUnit?: string | null;
  featureDefinitionId?: string | null;
}

export interface ReferenceDraft {
  label: string;
  projectId?: string | null;
  livingAreaSqftX100?: number | null;
  purchasePriceCents?: Cents | null;
  purchaseClosingCents?: Cents | null;
  statedRehabCents?: Cents | null;
  saleForecastCents?: Cents | null;
  arvCents?: Cents | null;
  sellingClosingCents?: Cents | null;
  interestAllowanceCents?: Cents | null;
  insuranceCents?: Cents | null;
  notes?: string;
  lines: ReferenceLineDraft[];
}

export interface ApplyOutcome {
  applied: Array<{ rateId: string; featureId: string; description: string; amountCents: Cents; updated: boolean }>;
  skipped: Array<{ rateId: string; description: string; reason: string }>;
}

/**
 * Spec §90-23: what a rate would estimate with today — the base figure, the
 * median of comparable finished work, and the number the two produce.
 */
export interface RatePrediction extends CalibratedRate {
  rate: ProfileRate;
  /** The finished projects behind the history (names only). */
  jobs: string[];
  lowCentsX100: number | null;
  highCentsX100: number | null;
}

/** What finished work of one kind, in one unit, actually cost. */
export interface RateHistory {
  medianUnitCostCentsX100: number;
  sampleSize: number;
  lowUnitCostCentsX100: number;
  highUnitCostCentsX100: number;
  /** The finished projects behind it (names only). */
  jobs: string[];
}

/** ONE FINISHED JOB'S EVIDENCE — the row a user inspects (spec §91-9). */
export interface HistoricalObservation {
  featureId: string;
  featureDefinitionId: string;
  featureName: string;
  projectId: string;
  projectName: string;
  /** When the renovation was called finished, when somebody did. */
  completedAt: string | null;
  /** definition | unit | surface — what may be compared with what. */
  key: string;
  unit: QuantityUnit;
  surface: RateSurface | null;
  quantityX100: number;
  /** Reconciled actual before sales tax, net of returns. */
  actualCents: Cents;
  unitCostCentsX100: number;
  /** Set when somebody asked estimating not to learn from it. */
  excluded: { reasonCode: string; reason: string } | null;
}

export interface RateEvidence extends CalibratedRate {
  rate: ProfileRate;
  /** Every job this rate may learn from, excluded ones included. */
  observations: HistoricalObservation[];
}

/** Why a company would not want a finished job to teach a price (spec §91-10). */
export type ObservationExclusionReason =
  | "unusual-condition"
  | "owner-labor"
  | "warranty-rework"
  | "emergency-premium"
  | "incomplete-attribution"
  | "other";

export const EXCLUSION_REASON_WORDS: Readonly<Record<ObservationExclusionReason, string>> = {
  "unusual-condition": "An unusual condition on that property",
  "owner-labor": "Work we did ourselves",
  "warranty-rework": "Warranty work or rework",
  "emergency-premium": "An emergency premium we would not normally pay",
  "incomplete-attribution": "The cost is not fully attributed to this work",
  other: "Not typical of how we normally work",
};

/** definition | unit | surface: the exact shape of a comparable observation. */
const observationKey = (definitionId: string, unit: string, surface: RateSurface | null): string =>
  `${definitionId}|${unit}|${surface ?? ""}`;

/**
 * DOES THIS FINISHED JOB TEACH THIS RATE? (spec §91-17)
 *
 * The kind of work must be the same and the UNIT must be the same: nothing
 * is converted, so $2.05 per square foot never learns from linear feet and
 * a roofing square never learns from a square foot.
 *
 * The SURFACE is deliberately weaker here than when a rate PRICES a
 * quantity (rateMatchesQuantity, which refuses an area whose surface is
 * unknown). There, refusing is right: nobody knows what is being measured.
 * Here the feature DEFINITION is already the guarantee — wall painting
 * measured in square feet is wall area whether or not the row recorded a
 * surface — so a stated surface only has to agree where one exists. Making
 * this strict would have thrown away real evidence and taught nothing
 * safer, because the key already pins the kind of work.
 */
function observationMatchesRate(rate: ProfileRate, observation: HistoricalObservation): boolean {
  if (rate.featureDefinitionId === null || rate.featureDefinitionId !== observation.featureDefinitionId) return false;
  if (rate.unit === null) {
    // A pre-§90 rate has no structural unit: it keeps the old behaviour of
    // learning from whatever its own work was measured in.
    return true;
  }
  if (isDimensionless(rate.unit)) return false; // an allowance has no unit to learn per
  const wanted = rate.unit === "system" ? "each" : rate.unit;
  if (wanted !== observation.unit) return false;
  if (rate.surface !== null && observation.surface !== null && rate.surface !== observation.surface) return false;
  return true;
}

function summarizeObservations(list: readonly HistoricalObservation[]): RateHistory | null {
  if (list.length === 0) return null;
  const sorted = [...list].sort((a, b) => a.unitCostCentsX100 - b.unitCostCentsX100);
  const mid = Math.floor(sorted.length / 2);
  const median =
    sorted.length % 2 === 1
      ? sorted[mid].unitCostCentsX100
      : Math.round((sorted[mid - 1].unitCostCentsX100 + sorted[mid].unitCostCentsX100) / 2);
  return {
    medianUnitCostCentsX100: median,
    sampleSize: sorted.length,
    lowUnitCostCentsX100: sorted[0].unitCostCentsX100,
    highUnitCostCentsX100: sorted[sorted.length - 1].unitCostCentsX100,
    jobs: [...new Set(list.map((o) => o.projectName))],
  };
}

export interface CalibrationView {
  profile: EstimatingProfile;
  reference: CalibrationReference;
  arithmetic: ReferenceArithmetic;
  comparison: CalibrationComparison;
  metrics: ProjectSqftMetrics;
  /** Reference benchmarks beside the project's own (spec §77M). */
  referenceMetrics: ProjectSqftMetrics;
}

const now = () => new Date().toISOString();

export class CalibrationService {
  constructor(
    private readonly db: DbExecutor,
    private readonly proposedCosts: ProposedCostRepository,
    private readonly categoryBudget: CategoryBudgetService,
    /**
     * Spec §91-8: learning consumes the SAME normalized actual-cost events
     * the project reconciles against — never a second reading of the
     * accounting. The dependency is one-way; reconciliation knows nothing
     * about estimating profiles.
     */
    private readonly reconciliation: ReconciliationService,
  ) {}

  /* ---------------- profiles ---------------- */

  async listProfiles(): Promise<EstimatingProfile[]> {
    const rows = await this.db.select<{ id: string; name: string; notes: string; created_at: string }>(
      "SELECT id, name, notes, created_at FROM estimating_profiles ORDER BY created_at",
    );
    return rows.map((r) => ({ id: r.id, name: r.name, notes: r.notes, createdAt: r.created_at }));
  }

  async createProfile(name: string, notes = ""): Promise<EstimatingProfile> {
    const id = generateId();
    const stamp = now();
    await this.db.execute(
      "INSERT INTO estimating_profiles (id, name, notes, created_at, updated_at) VALUES (?, ?, ?, ?, ?)",
      [id, name.trim(), notes, stamp, stamp],
    );
    return { id, name: name.trim(), notes, createdAt: stamp };
  }

  /* ---------------- calibration references ---------------- */

  /**
   * Create or replace one reference and its lines wholesale. The lines are
   * the SOURCE'S OWN figures: nothing here adjusts them, and the stated
   * total is stored beside them however the arithmetic works out.
   */
  async saveReference(profileId: string, draft: ReferenceDraft, referenceId?: string): Promise<string> {
    const id = referenceId ?? generateId();
    const stamp = now();
    await this.db.transaction(async () => {
      const existing = await this.db.select<{ id: string }>(
        "SELECT id FROM calibration_references WHERE id = ?",
        [id],
      );
      if (existing.length === 0) {
        await this.db.execute(
          `INSERT INTO calibration_references
             (id, profile_id, label, project_id, living_area_sqft_x100, purchase_price_cents,
              purchase_closing_cents, stated_rehab_cents, sale_forecast_cents, arv_cents,
              selling_closing_cents, interest_allowance_cents, insurance_cents, notes, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            id, profileId, draft.label.trim(), draft.projectId ?? null, draft.livingAreaSqftX100 ?? null,
            draft.purchasePriceCents ?? null, draft.purchaseClosingCents ?? null, draft.statedRehabCents ?? null,
            draft.saleForecastCents ?? null, draft.arvCents ?? null, draft.sellingClosingCents ?? null,
            draft.interestAllowanceCents ?? null, draft.insuranceCents ?? null, draft.notes ?? "", stamp, stamp,
          ],
        );
      } else {
        await this.db.execute(
          `UPDATE calibration_references SET label = ?, project_id = ?, living_area_sqft_x100 = ?,
             purchase_price_cents = ?, purchase_closing_cents = ?, stated_rehab_cents = ?,
             sale_forecast_cents = ?, arv_cents = ?, selling_closing_cents = ?,
             interest_allowance_cents = ?, insurance_cents = ?, notes = ?, updated_at = ? WHERE id = ?`,
          [
            draft.label.trim(), draft.projectId ?? null, draft.livingAreaSqftX100 ?? null,
            draft.purchasePriceCents ?? null, draft.purchaseClosingCents ?? null, draft.statedRehabCents ?? null,
            draft.saleForecastCents ?? null, draft.arvCents ?? null, draft.sellingClosingCents ?? null,
            draft.interestAllowanceCents ?? null, draft.insuranceCents ?? null, draft.notes ?? "", stamp, id,
          ],
        );
        await this.db.execute("DELETE FROM calibration_reference_lines WHERE reference_id = ?", [id]);
      }
      let sort = 0;
      for (const line of draft.lines) {
        await this.db.execute(
          `INSERT INTO calibration_reference_lines
             (id, reference_id, description, amount_cents, category_id, quantity_x100, quantity_unit,
              feature_definition_id, sort_order, created_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            generateId(), id, line.description.trim(), line.amountCents, line.categoryId,
            line.quantityX100 ?? null, line.quantityUnit ?? null, line.featureDefinitionId ?? null,
            (sort += 10), stamp,
          ],
        );
      }
    });
    return id;
  }

  async listReferences(profileId: string): Promise<CalibrationReference[]> {
    const [refRows, lineRows] = await Promise.all([
      this.db.select<{
        id: string; profile_id: string; label: string; project_id: string | null;
        living_area_sqft_x100: number | null; purchase_price_cents: number | null;
        purchase_closing_cents: number | null; stated_rehab_cents: number | null;
        sale_forecast_cents: number | null; arv_cents: number | null; selling_closing_cents: number | null;
        interest_allowance_cents: number | null; insurance_cents: number | null; notes: string;
      }>(
        "SELECT * FROM calibration_references WHERE profile_id = ? ORDER BY created_at",
        [profileId],
      ),
      this.db.select<{
        id: string; reference_id: string; description: string; amount_cents: number; category_id: string;
        quantity_x100: number | null; quantity_unit: string | null; feature_definition_id: string | null;
      }>(
        `SELECT l.* FROM calibration_reference_lines l
           JOIN calibration_references r ON r.id = l.reference_id
          WHERE r.profile_id = ? ORDER BY l.sort_order`,
        [profileId],
      ),
    ]);
    const linesByRef = new Map<string, ReferenceLineInput[]>();
    for (const row of lineRows) {
      const list = linesByRef.get(row.reference_id) ?? [];
      list.push({
        id: row.id,
        description: row.description,
        amountCents: Number(row.amount_cents),
        categoryId: row.category_id as TradeId | "other",
        quantityX100: row.quantity_x100 === null ? null : Number(row.quantity_x100),
        quantityUnit: row.quantity_unit,
        featureDefinitionId: row.feature_definition_id,
      });
      linesByRef.set(row.reference_id, list);
    }
    return refRows.map((r) => ({
      id: r.id,
      profileId: r.profile_id,
      label: r.label,
      projectId: r.project_id,
      livingAreaSqftX100: r.living_area_sqft_x100 === null ? null : Number(r.living_area_sqft_x100),
      purchasePriceCents: r.purchase_price_cents === null ? null : Number(r.purchase_price_cents),
      purchaseClosingCents: r.purchase_closing_cents === null ? null : Number(r.purchase_closing_cents),
      statedRehabCents: r.stated_rehab_cents === null ? null : Number(r.stated_rehab_cents),
      saleForecastCents: r.sale_forecast_cents === null ? null : Number(r.sale_forecast_cents),
      arvCents: r.arv_cents === null ? null : Number(r.arv_cents),
      sellingClosingCents: r.selling_closing_cents === null ? null : Number(r.selling_closing_cents),
      interestAllowanceCents: r.interest_allowance_cents === null ? null : Number(r.interest_allowance_cents),
      insuranceCents: r.insurance_cents === null ? null : Number(r.insurance_cents),
      notes: r.notes,
      lines: linesByRef.get(r.id) ?? [],
    }));
  }

  /* ---------------- rates ---------------- */

  /**
   * Derive the profile's rates from one reference (spec §77E). Explicit and
   * user-triggered — nothing recalibrates on its own, and rates the user
   * edited by hand keep their edited figures (their provenance says so).
   */
  async recalibrate(profileId: string, referenceId: string): Promise<ProfileRate[]> {
    const references = await this.listReferences(profileId);
    const reference = references.find((r) => r.id === referenceId);
    if (!reference) throw new Error("That calibration reference no longer exists.");
    const derived = deriveRates(toEngineReference(reference));
    const stamp = now();
    await this.db.transaction(async () => {
      // Replace only the rates that CAME from this reference; hand-written
      // rates (source_reference_id NULL) are the user's and stay.
      await this.db.execute(
        "DELETE FROM profile_rates WHERE profile_id = ? AND source_reference_id = ?",
        [profileId, referenceId],
      );
      for (const rate of derived) {
        await this.db.execute(
          `INSERT INTO profile_rates
             (id, profile_id, category_id, feature_definition_id, driver, rate_unit, rate_surface,
              rate_cents, provenance, source_reference_id, rate_origin, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'reference', ?, ?)`,
          [
            generateId(), profileId, rate.categoryId, rate.featureDefinitionId, rate.driver,
            rate.unit, rate.surface, rate.rateCents, rate.provenance, referenceId, stamp, stamp,
          ],
        );
      }
    });
    return this.listRates(profileId);
  }

  async listRates(profileId: string): Promise<ProfileRate[]> {
    const rows = await this.db.select<{
      id: string; profile_id: string; category_id: string; feature_definition_id: string | null;
      driver: string; rate_cents: number; provenance: string; source_reference_id: string | null;
      rate_unit: string | null; rate_surface: string | null; rate_origin: string | null;
    }>("SELECT * FROM profile_rates WHERE profile_id = ? ORDER BY category_id, feature_definition_id", [profileId]);
    return rows.map((r) => ({
      id: r.id,
      profileId: r.profile_id,
      categoryId: r.category_id as TradeId | "other",
      featureDefinitionId: r.feature_definition_id,
      driver: r.driver as RateDriver,
      unit: (r.rate_unit as RateUnit | null) ?? null,
      surface: (r.rate_surface as RateSurface | null) ?? null,
      rateCents: Number(r.rate_cents),
      provenance: r.provenance,
      sourceReferenceId: r.source_reference_id,
      origin: (r.rate_origin as RateOrigin) ?? null,
    }));
  }

  /** A hand edit: the figure changes, and the provenance says who decided. */
  async updateRate(rateId: string, rateCents: Cents, provenance: string): Promise<void> {
    await this.db.execute(
      "UPDATE profile_rates SET rate_cents = ?, provenance = ?, source_reference_id = NULL, updated_at = ? WHERE id = ?",
      [rateCents, provenance, now(), rateId],
    );
  }

  async deleteRate(rateId: string): Promise<void> {
    await this.db.execute("DELETE FROM profile_rates WHERE id = ?", [rateId]);
  }

  /**
   * THE PRICE I NORMALLY BUDGET (spec §91-16).
   *
   * §90 could only reach a base rate by reverse-engineering a reference
   * budget, so a user who simply knows they budget flooring at $5.25 a
   * square foot had to invent an artificial project first. This writes the
   * SAME profile_rates row a reference would, with its unit and surface
   * structural (§91-17) — so a $/wall-sq-ft figure still cannot price
   * linear feet — and its origin recorded as typed by hand.
   *
   * There is one rate per (profile, category, kind of work): entering a
   * figure for work that already has one EDITS that rate rather than
   * stacking a second, because two rates for one kind of work is exactly
   * how a price book starts competing with itself.
   */
  async setDirectRate(input: {
    profileId: string;
    categoryId: TradeId | "other";
    featureDefinitionId: string | null;
    unit: RateUnit;
    surface?: RateSurface | null;
    rateCents: Cents;
    note?: string;
  }): Promise<ProfileRate> {
    /*
     * FOUND IN ACCEPTANCE: a mistyped kind of work ("paint-ceilings" for
     * "paint-ceiling") was accepted, stored, and then rendered as a raw id
     * in a price list — a rate that could never price anything. A price has
     * to be a price FOR something.
     */
    if (input.featureDefinitionId !== null) {
      const known = await this.db.select<{ id: string }>("SELECT id FROM feature_definitions WHERE id = ?", [
        input.featureDefinitionId,
      ]);
      if (known.length === 0) {
        throw new ValidationError(["That kind of work no longer exists, so a price cannot be recorded for it."]);
      }
    }
    const surface = input.unit === "sqft" ? (input.surface ?? null) : null;
    const driver: RateDriver = input.unit === "house-sqft" ? "house-sqft" : input.unit === "allowance" ? "allowance" : "each";
    const provenance =
      (input.note && input.note.trim() !== "" ? input.note.trim() + " " : "") +
      `Entered by hand as ${rateUnitWords(input.unit, surface)}.`;
    const stamp = now();
    const existing = await this.db.select<{ id: string }>(
      `SELECT id FROM profile_rates
        WHERE profile_id = ? AND category_id = ?
          AND (feature_definition_id IS ? OR feature_definition_id = ?)`,
      [input.profileId, input.categoryId, input.featureDefinitionId, input.featureDefinitionId],
    );
    if (existing.length > 0) {
      await this.db.execute(
        `UPDATE profile_rates SET driver = ?, rate_unit = ?, rate_surface = ?, rate_cents = ?,
           provenance = ?, source_reference_id = NULL, rate_origin = 'manual', updated_at = ?
         WHERE id = ?`,
        [driver, input.unit, surface, input.rateCents, provenance, stamp, existing[0].id],
      );
      return (await this.listRates(input.profileId)).find((r) => r.id === existing[0].id)!;
    }
    const id = generateId();
    await this.db.execute(
      `INSERT INTO profile_rates
         (id, profile_id, category_id, feature_definition_id, driver, rate_unit, rate_surface,
          rate_cents, provenance, source_reference_id, rate_origin, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, NULL, 'manual', ?, ?)`,
      [
        id, input.profileId, input.categoryId, input.featureDefinitionId, driver,
        input.unit, surface, input.rateCents, provenance, stamp, stamp,
      ],
    );
    return (await this.listRates(input.profileId)).find((r) => r.id === id)!;
  }

  /**
   * CHANGING WHAT A LEGACY RATE IS PER (spec §91-40). A pre-§90 rate has no
   * structural unit, and §90's guarantee was that nothing would tell it what
   * it had been per. So the unit can only ever be set DELIBERATELY, by
   * somebody choosing it — never inferred from a label, a driver or a
   * neighbouring rate.
   */
  async setRateUnit(rateId: string, unit: RateUnit, surface: RateSurface | null = null): Promise<void> {
    const resolved = unit === "sqft" ? surface : null;
    await this.db.execute(
      `UPDATE profile_rates SET rate_unit = ?, rate_surface = ?,
         provenance = provenance || ' Its unit was confirmed as ' || ? || ' by hand.', updated_at = ?
       WHERE id = ?`,
      [unit, resolved, rateUnitWords(unit, resolved), now(), rateId],
    );
  }

  /* ---------------- what finished houses taught (spec §90-21) ---------------- */

  /**
   * WHAT COMPARABLE FINISHED WORK ACTUALLY COST, per natural unit.
   *
   * One bounded query over OTHER projects' finished work of the same kind,
   * in the same unit: the reconciled money linked to that work divided by
   * the quantity it bought. A rate is all-in, so material and labour are
   * summed exactly as the project recorded them — nothing is split or
   * inferred. Every safeguard the §9E history already had is kept:
   *
   *   - the work must be marked COMPLETE (a half-finished job teaches nothing);
   *   - a voided record is not money;
   *   - the quantity and unit must both be known;
   *   - the project being estimated never teaches itself;
   *   - an EXCLUDED line (spec §90-37) is not counted at all.
   *
   * The median is what history contributes, so one abnormal job among
   * several cannot drag the answer.
   */
  async rateHistory(options: { excludeProjectId?: string | null } = {}): Promise<Map<string, RateHistory>> {
    const observations = await this.observations(options);
    const byKey = new Map<string, HistoricalObservation[]>();
    for (const observation of observations) {
      if (observation.excluded !== null) continue;
      const list = byKey.get(observation.key) ?? [];
      list.push(observation);
      byKey.set(observation.key, list);
    }
    const out = new Map<string, RateHistory>();
    for (const [key, list] of byKey) out.set(key, summarizeObservations(list)!);
    return out;
  }

  /**
   * EVERY FINISHED JOB BEHIND A RATE, one row each (spec §91-9).
   *
   * The evidence itself, so a user can look at what the software claims to
   * have learned: which project, when it finished, what it actually cost,
   * over what quantity, and therefore what it cost per unit — including the
   * ones somebody excluded, which stay visible with their reason.
   *
   * WHERE THE MONEY COMES FROM. §90 read linked actual_expenses directly,
   * which quietly ignored every cost recorded as a PURCHASE — which is how
   * material is normally recorded, and the only way a read receipt records
   * it. It now consumes the same normalized ActualCostEvent stream the rest
   * of the application reconciles against, so allocations, returns (negative
   * events), voids, partial payments, several expenses against one item and
   * one expense split across items are all handled by the accounting's own
   * rules rather than by a second interpretation of them. The figure learned
   * is merchandise plus allocated shipping BEFORE sales tax — the §9E
   * historical basis, because the estimate adds its own estimating tax.
   *
   * BOUNDED: one query finds the projects that actually hold finished,
   * measured, priced work; only those projects' accounting is read. A
   * workspace whose projects are all in progress does no further work.
   */
  async observations(options: { excludeProjectId?: string | null } = {}): Promise<HistoricalObservation[]> {
    const rows = await this.db.select<{
      feature_id: string;
      feature_definition_id: string;
      feature_name: string;
      unit: string | null;
      binding: string | null;
      finish_key: string | null;
      project_id: string;
      project_name: string;
      completed_at: string | null;
      quantity_x100: number | null;
      geometry_x100: number | null;
      quantity_source: ProjectFeature["quantitySource"];
    }>(
      `SELECT f.id AS feature_id,
              f.feature_definition_id,
              f.name AS feature_name,
              f.quantity_unit AS unit,
              f.quantity_geometry_binding AS binding,
              f.finish_key,
              f.project_id,
              pr.name AS project_name,
              pr.renovation_completed_at AS completed_at,
              f.quantity_x100,
              f.quantity_geometry_x100 AS geometry_x100,
              f.quantity_source
         FROM project_features f
         JOIN projects pr ON pr.id = f.project_id
        WHERE f.archived_at IS NULL
          AND f.feature_definition_id IS NOT NULL
          AND f.work_status = 'complete'`,
    );
    const candidates = rows.flatMap(row => {
      if (options.excludeProjectId && row.project_id === options.excludeProjectId) return [];
      const effective=effectiveFeatureQuantity({quantityX100:row.quantity_x100,quantityUnit:row.unit as QuantityUnit|null,quantitySource:row.quantity_source,quantityGeometryBinding:row.binding as FeatureGeometryBinding|null,quantityGeometryX100:row.geometry_x100});
      if(effective.valueX100===null || effective.valueX100<=0 || effective.unit===null)return [];
      return [{...row,quantity_x100:effective.valueX100,unit:effective.unit}];
    });
    if (candidates.length === 0) return [];

    const exclusionRows = await this.db.select<{ feature_id: string; reason_code: string; reason: string }>(
      "SELECT feature_id, reason_code, reason FROM estimating_observation_exclusions",
    );
    const exclusions = new Map(exclusionRows.map((r) => [r.feature_id, { reasonCode: r.reason_code, reason: r.reason }]));

    // Only the projects that hold candidate work are read.
    const projectIds = [...new Set(candidates.map((r) => r.project_id))];
    const mixedLearningBlocked=await mixedSettlementLearningBlocked95(this.db,projectIds);
    const money = new Map<string, Cents>();
    for (const projectId of projectIds) {
      const [events, lines] = await Promise.all([
        this.reconciliation.eventsForProject(projectId),
        this.proposedCosts.listByProject(projectId),
      ]);
      const lineFeature = new Map(lines.map((l) => [l.id, l.featureId]));
      for (const event of events) {
        if (event.voidedAt !== null) continue;
        const featureId =
          event.linkedFeatureId ?? (event.linkedProposedLineId ? (lineFeature.get(event.linkedProposedLineId) ?? null) : null);
        if (featureId === null) continue;
        // Merchandise + allocated shipping, before sales tax (§9E).
        money.set(featureId, (money.get(featureId) ?? 0) + event.amountCents);
      }
    }

    const out: HistoricalObservation[] = [];
    for (const row of candidates) {
      if(mixedLearningBlocked.has(row.feature_id))continue;
      const actualCents = money.get(row.feature_id) ?? 0;
      if (actualCents <= 0) continue; // no money, or netted to nothing by returns
      const quantityX100 = Number(row.quantity_x100);
      const geometryShape = quantityShapeOf({
        quantity_unit: row.unit,
        quantity_geometry_binding: row.binding,
        finish_key: row.finish_key,
      });
      const shape = { ...geometryShape, unit: row.unit };
      if (shape.unit === null) continue;
      out.push({
        featureId: row.feature_id,
        featureDefinitionId: row.feature_definition_id,
        featureName: row.feature_name,
        projectId: row.project_id,
        projectName: row.project_name,
        completedAt: row.completed_at,
        key: observationKey(row.feature_definition_id, shape.unit, shape.surface),
        unit: shape.unit,
        surface: shape.surface,
        quantityX100,
        actualCents,
        unitCostCentsX100: Math.round((actualCents * 100 * 100) / quantityX100),
        excluded: exclusions.get(row.feature_id) ?? null,
      });
    }
    out.sort((a, b) => a.projectName.localeCompare(b.projectName) || a.featureName.localeCompare(b.featureName));
    return out;
  }

  /**
   * THE EVIDENCE BEHIND ONE RATE (spec §91-9): base, each finished job,
   * the median, the sample count and the figure the profile estimates with.
   */
  async rateEvidence(rateId: string, options: { excludeProjectId?: string | null } = {}): Promise<RateEvidence | null> {
    const rates = await this.db.select<{ profile_id: string }>("SELECT profile_id FROM profile_rates WHERE id = ?", [rateId]);
    if (rates.length === 0) return null;
    /*
     * EVERY job is LISTED; the summary counts only the ones that may teach
     * (spec §91-9). The two are deliberately different: a user inspecting a
     * rate wants to see their own finished houses, including the one they
     * are estimating and the ones they excluded, while the figure the
     * profile estimates with must leave both out. Fetching the list with
     * the exclusion applied hid rows the header had already counted, which
     * is how the hosted acceptance run saw "1 finished job" above a table
     * of two.
     */
    const [all, observations] = await Promise.all([this.listRates(rates[0].profile_id), this.observations()]);
    const rate = all.find((r) => r.id === rateId);
    if (!rate) return null;
    const mine = observations.filter((o) => observationMatchesRate(rate, o));
    const counted = mine.filter((o) => o.excluded === null && o.projectId !== options.excludeProjectId);
    return { rate, observations: mine, ...calibratedRate(rate.rateCents, summarizeObservations(counted)) };
  }

  /**
   * WHAT A PROFILE WOULD ESTIMATE WITH TODAY (spec §90-23): the base rate,
   * what finished jobs cost, and the number those two produce — with the
   * arithmetic in words. Nothing is stored: a prediction is derived, so a
   * project estimated last month keeps the figure it was estimated with.
   */
  async predictions(profileId: string, options: { excludeProjectId?: string | null } = {}): Promise<RatePrediction[]> {
    const [rates, observations] = await Promise.all([this.listRates(profileId), this.observations(options)]);
    const live = observations.filter((o) => o.excluded === null);
    return rates.map((rate) => {
      // A rate only learns from work of its own kind measured in its own
      // unit and, where it names one, on its own surface — the §90 rule,
      // asked of each observation rather than of a flattened key.
      const found = summarizeObservations(live.filter((o) => observationMatchesRate(rate, o)));
      const calibrated = calibratedRate(rate.rateCents, found);
      return {
        rate,
        ...calibrated,
        jobs: found?.jobs ?? [],
        lowCentsX100: found?.lowUnitCostCentsX100 ?? null,
        highCentsX100: found?.highUnitCostCentsX100 ?? null,
      };
    });
  }

  /* ---------------- what history may learn from ---------------- */

  /**
   * DO NOT LEARN FROM THIS JOB (spec §90-37). Donated labour, a favour, a
   * fire rebuild: the money is real and stays exactly where it is — this
   * records only that estimating should stop counting that line. Reversible.
   */
  async excludeFromHistory(projectId: string, proposedLineId: string, reason: string): Promise<void> {
    /*
     * §91-10 REFINEMENT. §90 recorded this against the estimate LINE, and
     * the history query then subtracted that line's money from its work
     * item — which quietly made the item look CHEAPER per unit instead of
     * removing it, the one thing an exclusion must never do. An observation
     * is a whole work item, so the decision is now recorded against the
     * item as well. The §90 row is still written, so nothing that existed
     * is lost.
     */
    const lines = await this.db.select<{ feature_id: string | null }>(
      "SELECT feature_id FROM proposed_cost_lines WHERE id = ?",
      [proposedLineId],
    );
    const featureId = lines[0]?.feature_id ?? null;
    if (featureId) {
      await this.excludeObservation(projectId, featureId, { code: "other", note: reason });
    }
    const existing = await this.db.select<{ id: string }>(
      "SELECT id FROM estimating_history_exclusions WHERE proposed_line_id = ?",
      [proposedLineId],
    );
    if (existing.length > 0) {
      await this.db.execute("UPDATE estimating_history_exclusions SET reason = ? WHERE proposed_line_id = ?", [reason, proposedLineId]);
      return;
    }
    await this.db.execute(
      "INSERT INTO estimating_history_exclusions (id, project_id, proposed_line_id, reason, created_at) VALUES (?, ?, ?, ?, ?)",
      [generateId(), projectId, proposedLineId, reason, now()],
    );
  }

  async includeInHistory(proposedLineId: string): Promise<void> {
    await this.db.execute("DELETE FROM estimating_history_exclusions WHERE proposed_line_id = ?", [proposedLineId]);
    const lines = await this.db.select<{ feature_id: string | null }>(
      "SELECT feature_id FROM proposed_cost_lines WHERE id = ?",
      [proposedLineId],
    );
    const featureId = lines[0]?.feature_id ?? null;
    if (featureId) await this.includeObservation(featureId);
  }

  /**
   * DO NOT LEARN FROM THIS ONE (spec §91-10), at the grain an observation
   * actually has: the whole work item, its whole reconciled cost over its
   * whole quantity. Nothing else moves — the expense, the quote, the
   * project's figures and the completed project are all exactly as they
   * were, and deleting this row counts the job again.
   */
  async excludeObservation(
    projectId: string,
    featureId: string,
    reason: { code: ObservationExclusionReason; note?: string },
  ): Promise<void> {
    const existing = await this.db.select<{ id: string }>(
      "SELECT id FROM estimating_observation_exclusions WHERE feature_id = ?",
      [featureId],
    );
    const note = reason.note?.trim() ?? "";
    const text = note === "" ? (EXCLUSION_REASON_WORDS[reason.code] ?? "") : note;
    if (existing.length > 0) {
      await this.db.execute(
        "UPDATE estimating_observation_exclusions SET reason_code = ?, reason = ? WHERE feature_id = ?",
        [reason.code, text, featureId],
      );
      return;
    }
    await this.db.execute(
      `INSERT INTO estimating_observation_exclusions (id, project_id, feature_id, reason_code, reason, created_at)
       VALUES (?, ?, ?, ?, ?, ?)`,
      [generateId(), projectId, featureId, reason.code, text, now()],
    );
  }

  /** Count it again. An exclusion is a decision, so it is always reversible. */
  async includeObservation(featureId: string): Promise<void> {
    await this.db.execute("DELETE FROM estimating_observation_exclusions WHERE feature_id = ?", [featureId]);
  }

  async observationExclusions(projectId?: string): Promise<
    Array<{ featureId: string; projectId: string; reasonCode: ObservationExclusionReason; reason: string }>
  > {
    const rows = projectId
      ? await this.db.select<{ feature_id: string; project_id: string; reason_code: string; reason: string }>(
          "SELECT feature_id, project_id, reason_code, reason FROM estimating_observation_exclusions WHERE project_id = ?",
          [projectId],
        )
      : await this.db.select<{ feature_id: string; project_id: string; reason_code: string; reason: string }>(
          "SELECT feature_id, project_id, reason_code, reason FROM estimating_observation_exclusions",
        );
    return rows.map((r) => ({
      featureId: r.feature_id,
      projectId: r.project_id,
      reasonCode: r.reason_code as ObservationExclusionReason,
      reason: r.reason,
    }));
  }

  async historyExclusions(projectId?: string): Promise<Array<{ proposedLineId: string; projectId: string; reason: string }>> {
    const rows = projectId
      ? await this.db.select<{ proposed_line_id: string; project_id: string; reason: string }>(
          "SELECT proposed_line_id, project_id, reason FROM estimating_history_exclusions WHERE project_id = ?",
          [projectId],
        )
      : await this.db.select<{ proposed_line_id: string; project_id: string; reason: string }>(
          "SELECT proposed_line_id, project_id, reason FROM estimating_history_exclusions",
        );
    return rows.map((r) => ({ proposedLineId: r.proposed_line_id, projectId: r.project_id, reason: r.reason }));
  }

  /* ---------------- the project side ---------------- */

  async setProjectProfile(projectId: string, profileId: string | null): Promise<void> {
    await this.db.execute("UPDATE projects SET estimating_profile_id = ?, updated_at = ? WHERE id = ?", [
      profileId, now(), projectId,
    ]);
  }

  async setLivingArea(projectId: string, sqftX100: number | null): Promise<void> {
    await this.db.execute("UPDATE projects SET living_area_sqft_x100 = ?, updated_at = ? WHERE id = ?", [
      sqftX100, now(), projectId,
    ]);
  }

  /** Which profile this project estimates with — null when none. */
  async projectProfile(projectId: string): Promise<EstimatingProfile | null> {
    const rows = await this.db.select<{ id: string; name: string; notes: string; created_at: string }>(
      `SELECT p.id, p.name, p.notes, p.created_at
         FROM projects pr JOIN estimating_profiles p ON p.id = pr.estimating_profile_id
        WHERE pr.id = ?`,
      [projectId],
    );
    return rows.length === 0 ? null : { id: rows[0].id, name: rows[0].name, notes: rows[0].notes, createdAt: rows[0].created_at };
  }

  /** $/sq ft benchmarks (spec §77L). Unknown area → every figure "—". */
  async metrics(projectId: string): Promise<ProjectSqftMetrics & { strategy: "flip" | "rental" }> {
    const [projectRows, saleRows, budget] = await Promise.all([
      this.db.select<{
        purchase_price_cents: number | null; expected_arv_cents: number | null;
        living_area_sqft_x100: number | null; strategy: string;
      }>(
        "SELECT purchase_price_cents, expected_arv_cents, living_area_sqft_x100, strategy FROM projects WHERE id = ?",
        [projectId],
      ),
      this.db.select<{ gross_sale_price_cents: number }>(
        "SELECT gross_sale_price_cents FROM project_sales WHERE project_id = ?",
        [projectId],
      ),
      this.categoryBudget.model(projectId),
    ]);
    const project = projectRows[0];
    if (!project) throw new Error("That project no longer exists.");
    const strategy: "flip" | "rental" = project.strategy === "rental" ? "rental" : "flip";
    return {
      strategy,
      ...projectSqftMetrics({
        livingAreaSqftX100: project.living_area_sqft_x100 === null ? null : Number(project.living_area_sqft_x100),
        purchasePriceCents: project.purchase_price_cents === null ? null : Number(project.purchase_price_cents),
        renovationBasisCents: budget.totals.currentBasisCents,
        expectedValueCents: project.expected_arv_cents === null ? null : Number(project.expected_arv_cents),
        saleForecastCents: saleRows.length > 0 ? Number(saleRows[0].gross_sale_price_cents) : null,
        strategy,
      }),
    };
  }

  /**
   * The calibration comparison (spec §77G): this project's CALCULATED
   * estimate beside its profile's reference budget, category by category.
   * Null when the project has no profile or the profile has no reference —
   * a project without a profile keeps working exactly as before.
   */
  async compare(projectId: string): Promise<CalibrationView | null> {
    const projectRows = await this.db.select<{
      estimating_profile_id: string | null; living_area_sqft_x100: number | null;
      purchase_price_cents: number | null; expected_arv_cents: number | null; strategy: string;
    }>(
      `SELECT estimating_profile_id, living_area_sqft_x100, purchase_price_cents, expected_arv_cents, strategy
         FROM projects WHERE id = ?`,
      [projectId],
    );
    const project = projectRows[0];
    if (!project?.estimating_profile_id) return null;

    const profileRows = await this.db.select<{ id: string; name: string; notes: string; created_at: string }>(
      "SELECT id, name, notes, created_at FROM estimating_profiles WHERE id = ?",
      [project.estimating_profile_id],
    );
    if (profileRows.length === 0) return null;
    const references = await this.listReferences(project.estimating_profile_id);
    if (references.length === 0) return null;
    // Several references per profile are supported (spec §77R); today's
    // comparison reads the newest.
    const reference = references[references.length - 1];

    const budget = await this.categoryBudget.model(projectId);
    /* The budget model only carries categories with MONEY; scope that is
     * not yet priced still counts as scope for the comparison (a project
     * with unpriced tile work has tile scope the reference lacks), so the
     * features are resolved to categories by the same §74 rules. */
    const featureCounts = await this.featureCountsByCategory(projectId);
    const calculatedByCategory = new Map(
      budget.rows.filter((row) => row.categoryId !== "unassigned-money").map((row) => [row.categoryId, row]),
    );
    const categoryIds = [...new Set([...calculatedByCategory.keys(), ...featureCounts.keys()])];
    const comparison = compareToReference(
      reference.lines,
      categoryIds.map((categoryId) => {
        const row = calculatedByCategory.get(categoryId);
        return {
          categoryId,
          displayName: row?.displayName ?? categoryId,
          // The engine's OWN calculation — quotes/authorized/actual are
          // different concepts and stay in their own columns (spec §77O).
          calculatedCents: row?.calculatedCents ?? 0,
          featureCount: featureCounts.get(categoryId) ?? row?.featureIds.length ?? 0,
        };
      }),
    );

    const strategy: "flip" | "rental" = project.strategy === "rental" ? "rental" : "flip";
    const area = project.living_area_sqft_x100 === null ? null : Number(project.living_area_sqft_x100);
    return {
      profile: { id: profileRows[0].id, name: profileRows[0].name, notes: profileRows[0].notes, createdAt: profileRows[0].created_at },
      reference,
      arithmetic: referenceArithmetic(toEngineReference(reference)),
      comparison,
      metrics: projectSqftMetrics({
        livingAreaSqftX100: area,
        purchasePriceCents: project.purchase_price_cents === null ? null : Number(project.purchase_price_cents),
        renovationBasisCents: budget.totals.calculatedCents,
        expectedValueCents: project.expected_arv_cents === null ? null : Number(project.expected_arv_cents),
        saleForecastCents: null,
        strategy,
      }),
      referenceMetrics: projectSqftMetrics({
        livingAreaSqftX100: reference.livingAreaSqftX100,
        purchasePriceCents: reference.purchasePriceCents,
        renovationBasisCents: reference.statedRehabCents,
        expectedValueCents: reference.arvCents,
        saleForecastCents: reference.saleForecastCents,
        strategy: "flip",
      }),
    };
  }

  /** Features per §74 category — the same resolution rules as the budget. */
  private async featureCountsByCategory(projectId: string): Promise<Map<string, number>> {
    const byFeature = await this.featureCategoryMap(projectId);
    const counts = new Map<string, number>();
    for (const category of byFeature.values()) counts.set(category, (counts.get(category) ?? 0) + 1);
    return counts;
  }

  /**
   * Which §74 category each ACTIVE feature belongs to — resolved by the
   * category budget's own rules (demolition by action, decisions, combined
   * trades), never by whether the feature has money yet. Both compare()
   * and applyRates() read categories through this one map, so an unpriced
   * feature is still SCOPE everywhere (found by test: the budget model
   * only carries categories that already have lines).
   */
  private async featureCategoryMap(projectId: string): Promise<Map<string, string>> {
    const activeRows = await this.db.select<{ active_design_id: string | null }>(
      "SELECT active_design_id FROM projects WHERE id = ?",
      [projectId],
    );
    const designId = activeRows[0]?.active_design_id ?? null;
    const [featureRows, decisionRows, tradeRows] = await Promise.all([
      this.db.select<{ id: string; origin_id: string | null; feature_definition_id: string | null; action: string }>(
        `SELECT id, origin_id, feature_definition_id, action FROM project_features
          WHERE project_id = ? AND archived_at IS NULL AND (design_id IS NULL OR design_id = ?)`,
        [projectId, designId],
      ),
      this.db.select<{ root_id: string; trade_override: string | null }>(
        "SELECT root_id, trade_override FROM bid_decisions WHERE project_id = ? AND (design_id IS ? OR design_id = ?)",
        [projectId, designId, designId],
      ),
      this.db.select<{ trade_id: string; combine_with: string | null }>(
        "SELECT trade_id, combine_with FROM bid_trade_settings WHERE project_id = ? AND (design_id IS ? OR design_id = ?)",
        [projectId, designId, designId],
      ),
    ]);
    const decisions = new Map<string, TradeId>();
    for (const row of decisionRows) if (row.trade_override) decisions.set(row.root_id, row.trade_override as TradeId);
    const combines = new Map<TradeId, TradeId>();
    for (const row of tradeRows) {
      if (row.combine_with && row.combine_with !== row.trade_id) {
        combines.set(row.trade_id as TradeId, row.combine_with as TradeId);
      }
    }
    const byFeature = new Map<string, string>();
    for (const feature of featureRows) {
      byFeature.set(
        feature.id,
        resolveCategory(
          { rootId: feature.origin_id ?? feature.id, featureDefinitionId: feature.feature_definition_id, action: feature.action },
          decisions,
          combines,
        ),
      );
    }
    return byFeature;
  }

  /**
   * Apply the profile's rates to THIS project's existing scope (spec §77K).
   *
   * Explicit, owner-triggered, and honest about every skip:
   *   • a rate lands only where the project already HAS the scope — it
   *     never creates a feature (spec §77N);
   *   • work the detailed estimate has already priced is left alone — a
   *     calibrated figure never stacks on an assembly calculation;
   *   • re-applying updates this profile's own lines in place, so nothing
   *     is ever counted twice.
   * The lines it writes are ORDINARY proposed lines, so quotes, authorized
   * budgets, actuals, §75 and the Capital Plan follow automatically.
   */
  async applyRates(projectId: string): Promise<ApplyOutcome> {
    const projectRows = await this.db.select<{
      estimating_profile_id: string | null; living_area_sqft_x100: number | null;
    }>("SELECT estimating_profile_id, living_area_sqft_x100 FROM projects WHERE id = ?", [projectId]);
    const project = projectRows[0];
    if (!project?.estimating_profile_id) {
      return { applied: [], skipped: [{ rateId: "", description: "No estimating profile", reason: "This project has no estimating profile selected." }] };
    }
    const [rates, featureCategory] = await Promise.all([
      this.listRates(project.estimating_profile_id),
      this.featureCategoryMap(projectId),
    ]);
    const activeDesignRows = await this.db.select<{ active_design_id: string | null }>(
      "SELECT active_design_id FROM projects WHERE id = ?",
      [projectId],
    );
    const activeDesignId = activeDesignRows[0]?.active_design_id ?? null;
    const [featureRows, lines] = await Promise.all([
      this.db.select<{
        id: string; name: string; feature_definition_id: string | null;
        quantity_x100: number | null; quantity_geometry_x100: number | null; sort_order: number;
        quantity_unit: string | null; quantity_geometry_binding: string | null; finish_key: string | null;
      }>(
        `SELECT id, name, feature_definition_id, quantity_x100, quantity_geometry_x100, sort_order,
                quantity_unit, quantity_geometry_binding, finish_key
           FROM project_features
          WHERE project_id = ? AND archived_at IS NULL AND (design_id IS NULL OR design_id = ?)
          ORDER BY sort_order, created_at`,
        [projectId, activeDesignId],
      ),
      this.proposedCosts.listForDesign(projectId, activeDesignId),
    ]);
    const active = lines.filter((l) => l.archivedAt === null);
    const calibrationLineByKey = new Map(
      active.filter((l) => l.calibrationRateId !== null).map((l) => [`${l.calibrationRateId}|${l.featureId ?? ""}`, l]),
    );
    const otherMoneyByFeature = new Map<string, Cents>();
    for (const l of active) {
      if (l.calibrationRateId === null && l.featureId) {
        otherMoneyByFeature.set(l.featureId, (otherMoneyByFeature.get(l.featureId) ?? 0) + l.amountCents);
      }
    }
    const featuresByDefinition = new Map<string, typeof featureRows>();
    for (const f of featureRows) {
      if (!f.feature_definition_id) continue;
      const list = featuresByDefinition.get(f.feature_definition_id) ?? [];
      list.push(f);
      featuresByDefinition.set(f.feature_definition_id, list);
    }
    const featuresByCategory = new Map<string, typeof featureRows>();
    for (const f of featureRows) {
      const category = featureCategory.get(f.id);
      if (!category) continue;
      const list = featuresByCategory.get(category) ?? [];
      list.push(f);
      featuresByCategory.set(category, list);
    }

    /* ONE CALIBRATED FIGURE PER FEATURE, EVER. Two rates aimed at the
     * same scope (say, two per-each door rates on one category) must not
     * stack — the first wins and the second says so. A feature already
     * carrying another rate's line from an earlier run is equally taken. */
    const claimed = new Map<string, string>();
    for (const line of active) {
      if (line.calibrationRateId !== null && line.featureId) claimed.set(line.featureId, line.calibrationRateId);
    }

    /* THE SHARPEST RATE WINS (spec §77E): a rate naming a specific kind
     * of work ($600/exterior door) is considered before a category-wide
     * one, so the category rate covers only what nothing sharper did. */
    const ordered = [...rates].sort((a, b) =>
      (a.featureDefinitionId === null ? 1 : 0) - (b.featureDefinitionId === null ? 1 : 0),
    );

    const outcome: ApplyOutcome = { applied: [], skipped: [] };
    /*
     * A NEW PROJECT IS PRICED WITH WHAT THE COMPANY NOW PAYS (spec §90-38):
     * the base rate moved part of the way towards comparable finished work.
     * Existing lines are only rewritten when somebody applies again, so a
     * project estimated before the learning keeps its own figures.
     */
    const predictions = new Map(
      (await this.predictions(project.estimating_profile_id, { excludeProjectId: projectId })).map((p) => [p.rate.id, p]),
    );
    await this.db.transaction(async () => {
      for (const rate of ordered) {
        const prediction = predictions.get(rate.id) ?? null;
        const effectiveRateCents = prediction?.predictedCents ?? rate.rateCents;
        const targets = rate.featureDefinitionId
          ? (featuresByDefinition.get(rate.featureDefinitionId) ?? [])
          : (featuresByCategory.get(rate.categoryId) ?? []);
        if (targets.length === 0) {
          // No scope, no line: the profile NEVER creates scope (§77N).
          outcome.skipped.push({
            rateId: rate.id,
            description: rate.provenance.split(" — ")[0] ?? rate.categoryId,
            reason: `No ${rate.featureDefinitionId ?? rate.categoryId} scope in this project — nothing was created.`,
          });
          continue;
        }

        if (rate.driver === "each") {
          for (const feature of targets) {
            /*
             * A RATE ONLY PRICES WHAT IT IS PER (spec §90-20). Before §90
             * every stated unit collapsed to "each", so $58/sq ft of
             * countertop could be multiplied by 24 linear feet of cabinet
             * run. A rate that states its unit now has to fit the scope's
             * own quantity — and a pre-§90 rate (no unit) keeps its old
             * behaviour rather than being reinterpreted.
             */
            if (rate.unit !== null) {
              const shape = quantityShapeOf(feature);
              /*
               * §91-17 REFINEMENT. rateMatchesQuantity refuses an area whose
               * SURFACE is unknown, which is right for a rate that belongs to
               * a whole trade: nobody knows what those square feet measure.
               * For a rate that names the KIND OF WORK, the definition is
               * already the guarantee — a flooring rate can only meet
               * flooring — so a hand-typed "1,320 sq ft" is allowed to take
               * it while a quantity that STATES a different surface is still
               * refused. Without this, typing a quantity (the whole point of
               * §89) silently got no price from a rate typed for exactly
               * that work.
               */
              const surfaceKnownOrImplied =
                rate.featureDefinitionId !== null && rate.featureDefinitionId === feature.feature_definition_id && shape.surface === null
                  ? { ...shape, surface: rate.surface }
                  : shape;
              if (!rateMatchesQuantity({ unit: rate.unit, surface: rate.surface }, surfaceKnownOrImplied)) {
                outcome.skipped.push({
                  rateId: rate.id,
                  description: feature.name,
                  reason: `This rate is ${rateUnitText(rate)} and ${feature.name} is measured in ${shape.unit ?? "no stated unit"}${shape.surface ? ` (${shape.surface})` : ""} — nothing is converted behind your back.`,
                });
                continue;
              }
            }
            const holder = claimed.get(feature.id);
            if (holder !== undefined && holder !== rate.id) {
              outcome.skipped.push({
                rateId: rate.id,
                description: feature.name,
                reason: `${feature.name} is already covered by another calibrated rate — one figure per feature, never two.`,
              });
              continue;
            }
            if ((otherMoneyByFeature.get(feature.id) ?? 0) !== 0) {
              outcome.skipped.push({
                rateId: rate.id,
                description: feature.name,
                reason: `${feature.name} is already priced by the detailed estimate — the calibrated rate never stacks on it.`,
              });
              continue;
            }
            /* A feature with no quantity is priced as ONE unit, and the
             * line SAYS SO (the §75 rule: a default is a labelled
             * assumption, never silent fact). Setting the quantity — by
             * hand or from the design — re-prices it on the next apply. */
            const quantityX100 = feature.quantity_geometry_x100 ?? feature.quantity_x100;
            const assumed = quantityX100 === null;
            const amount = Math.round((effectiveRateCents * Number(quantityX100 ?? 100)) / 100);
            await this.writeLine(
              projectId, rate, feature.id,
              assumed ? `${feature.name} (assumed 1 each — set the feature quantity to change this)` : feature.name,
              amount, calibrationLineByKey, outcome,
            );
            claimed.set(feature.id, rate.id);
          }
          continue;
        }

        // Category-level figures: one line, on the first feature of the
        // scope, covering the whole category — skipped entirely when the
        // category already carries non-calibration money.
        const claimedByOther = targets.find((f) => {
          const holder = claimed.get(f.id);
          return holder !== undefined && holder !== rate.id;
        });
        if (claimedByOther) {
          outcome.skipped.push({
            rateId: rate.id,
            description: rate.categoryId,
            reason: `${rate.categoryId} scope is already covered by another calibrated rate — one figure per feature, never two.`,
          });
          continue;
        }
        const categoryMoney = targets.reduce((sum, f) => sum + (otherMoneyByFeature.get(f.id) ?? 0), 0);
        if (categoryMoney !== 0) {
          outcome.skipped.push({
            rateId: rate.id,
            description: rate.categoryId,
            reason: `${rate.categoryId} is already priced by the detailed estimate ($${(categoryMoney / 100).toLocaleString("en-US")}) — the calibrated figure never stacks on it.`,
          });
          continue;
        }
        let amount: Cents;
        if (rate.driver === "house-sqft") {
          if (project.living_area_sqft_x100 === null) {
            outcome.skipped.push({
              rateId: rate.id,
              description: rate.categoryId,
              reason: "This rate is per house square foot and the project's living area is not recorded.",
            });
            continue;
          }
          amount = Math.round((effectiveRateCents * Number(project.living_area_sqft_x100)) / 100);
        } else {
          amount = effectiveRateCents;
        }
        const first = targets[0];
        await this.writeLine(projectId, rate, first.id, `${first.name} (category allowance)`, amount, calibrationLineByKey, outcome);
        claimed.set(first.id, rate.id);
      }
    });
    return outcome;
  }

  private async writeLine(
    projectId: string,
    rate: ProfileRate,
    featureId: string,
    label: string,
    amountCents: Cents,
    existing: Map<string, { id: string; amountCents: Cents }>,
    outcome: ApplyOutcome,
  ): Promise<void> {
    const description = `Calibrated: ${label}`;
    const key = `${rate.id}|${featureId}`;
    const found = existing.get(key);
    if (found) {
      if (found.amountCents !== amountCents) {
        await this.db.execute(
          "UPDATE proposed_cost_lines SET amount_cents = ?, description = ?, updated_at = ? WHERE id = ?",
          [amountCents, description, now(), found.id],
        );
      }
      outcome.applied.push({ rateId: rate.id, featureId, description, amountCents, updated: true });
      return;
    }
    const line = await this.proposedCosts.create(projectId, {
      description,
      amountCents,
      categoryId: null,
      costType: null,
      featureId,
      notes: rate.provenance,
    });
    await this.db.execute("UPDATE proposed_cost_lines SET calibration_rate_id = ? WHERE id = ?", [rate.id, line.id]);
    outcome.applied.push({ rateId: rate.id, featureId, description, amountCents, updated: false });
  }
}

/** The unit (and surface) a feature's quantity is stated in (spec §90). */
function quantityShapeOf(feature: {
  quantity_unit: string | null;
  quantity_geometry_binding: string | null;
  finish_key: string | null;
}): { unit: QuantityUnit | null; surface: RateSurface | null } {
  const binding = (feature.quantity_geometry_binding as FeatureGeometryBinding | null) ?? null;
  const unit = binding ? geometryBindingUnit(binding) : ((feature.quantity_unit as QuantityUnit | null) ?? null);
  // A §82 project finish says which surface it covers in its own finish_key.
  const finishSurface = feature.finish_key?.startsWith("walls:")
    ? ("wall" as RateSurface)
    : feature.finish_key?.startsWith("ceiling:")
      ? ("ceiling" as RateSurface)
      : feature.finish_key?.startsWith("floor:")
        ? ("floor" as RateSurface)
        : null;
  return { unit, surface: finishSurface ?? surfaceOfBinding(binding) };
}

/** "per wall sq ft" — for the sentence that explains a skip. */
function rateUnitText(rate: { unit: RateUnit | null; surface: RateSurface | null }): string {
  return rateUnitWords(rate.unit, rate.surface);
}

function toEngineReference(reference: CalibrationReference): CalibrationReferenceInput {
  return {
    id: reference.id,
    label: reference.label,
    livingAreaSqftX100: reference.livingAreaSqftX100,
    purchasePriceCents: reference.purchasePriceCents,
    purchaseClosingCents: reference.purchaseClosingCents,
    statedRehabCents: reference.statedRehabCents,
    saleForecastCents: reference.saleForecastCents,
    arvCents: reference.arvCents,
    lines: reference.lines,
  };
}
